import { projectGameStateForViewer } from "@cloudnest/one-night-ultimate-werewolf-engine";
import { useEffect, useMemo, useState } from "react";
import { navigateTo } from "../../router";
import { OneNightEventLog } from "./components/OneNightEventLog";
import { OneNightRosterBoard } from "./components/OneNightRosterBoard";
import { deriveCumulativeLog } from "./event-log";
import { outcomeReasonLabels, phaseLabels, teamLabels } from "./labels";
import { ONE_NIGHT_ULTIMATE_WEREWOLF_REPLAY_FIXTURES } from "./replay-fixtures.generated";
import {
  buildOneNightReplaySnapshots,
  type OneNightReplaySnapshot,
  projectGodView,
} from "./replay-runner";
import "./styles/one-night-ultimate-werewolf-tokens.css";

interface ReplayScreenProps {
  readonly scenario: string;
}

const SPEED_OPTIONS = [
  { label: "느리게", ms: 1200 },
  { label: "보통", ms: 700 },
  { label: "빠르게", ms: 350 },
] as const;

const PHASE_MODE_CHIPS: Readonly<Record<string, string>> = {
  setup: "⚙ 준비",
  "role-peek": "⚙ 준비",
  night: "🌙 밤",
  day: "☀️ 낮",
  vote: "🗳️ 투표",
  reveal: "🏁 결과",
  complete: "🏁 결과",
};

function clampStepIndex(
  index: number,
  snapshots: readonly OneNightReplaySnapshot[],
): number {
  if (snapshots.length === 0) return 0;
  return Math.min(Math.max(0, index), snapshots.length - 1);
}

function parseInteger(value: string | null): number | null {
  if (value === null || value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) ? parsed : null;
}

function resolveStepIndexFromSearch(
  snapshots: readonly OneNightReplaySnapshot[],
): number {
  if (typeof window === "undefined" || snapshots.length === 0) return 0;
  const params = new URLSearchParams(window.location.search);
  const seq = parseInteger(params.get("seq"));
  if (seq !== null) {
    const index = snapshots.findIndex((s) => s.event.sequenceNumber === seq);
    if (index >= 0) return index;
  }
  const step = parseInteger(params.get("step"));
  if (step !== null) return clampStepIndex(step, snapshots);
  return 0;
}

function replaceStepQuery(
  index: number,
  snapshots: readonly OneNightReplaySnapshot[],
): void {
  if (typeof window === "undefined" || snapshots.length === 0) return;
  const url = new URL(window.location.href);
  url.searchParams.set("step", String(index));
  url.searchParams.delete("seq");
  window.history.replaceState(null, "", `${url.pathname}${url.search}`);
}

function eventSummary(
  snapshot: OneNightReplaySnapshot,
  namesByPlayerId: Readonly<Record<string, string>>,
): string {
  const event = snapshot.event;
  const actor = namesByPlayerId[event.userId] ?? event.userId;
  if (event.eventType === "action") {
    const action = (
      event.payload as { action?: { type?: string; roleId?: string } }
    ).action;
    const roleSuffix = action?.roleId ? ` · ${action.roleId}` : "";
    return `#${event.sequenceNumber} ${action?.type ?? "action"}${roleSuffix} · ${actor}`;
  }
  return `#${event.sequenceNumber} ${event.eventType} · ${actor}`;
}

export function ReplayScreen({ scenario }: ReplayScreenProps) {
  const fixture = ONE_NIGHT_ULTIMATE_WEREWOLF_REPLAY_FIXTURES[scenario] ?? null;
  const snapshots = useMemo(
    () =>
      fixture
        ? buildOneNightReplaySnapshots(fixture.events, {
            observerUserId: fixture.observerUserId,
          })
        : [],
    [fixture],
  );
  const cumulativeLogs = useMemo(
    () => deriveCumulativeLog(snapshots.map((s) => s.observerView)),
    [snapshots],
  );
  const namesByPlayerId = useMemo(
    () =>
      fixture
        ? Object.fromEntries(fixture.players.map((p) => [p.id, p.name]))
        : {},
    [fixture],
  );

  const [stepIndex, setStepIndex] = useState(() =>
    resolveStepIndexFromSearch(snapshots),
  );
  const [isPlaying, setIsPlaying] = useState(false);
  const [speedMs, setSpeedMs] = useState<number>(SPEED_OPTIONS[1].ms);
  const [showRecap, setShowRecap] = useState(false);
  const [logExpanded, setLogExpanded] = useState(false);
  const [perspectiveId, setPerspectiveId] = useState<string>("observer");

  const safeStepIndex = clampStepIndex(stepIndex, snapshots);
  const current = snapshots[safeStepIndex] ?? snapshots[0] ?? null;
  const maxIndex = snapshots.length - 1;

  useEffect(() => {
    setStepIndex(resolveStepIndexFromSearch(snapshots));
  }, [snapshots]);

  useEffect(() => {
    if (!isPlaying) return;
    if (safeStepIndex >= maxIndex) {
      setIsPlaying(false);
      return;
    }
    const timer = setTimeout(() => {
      const next = clampStepIndex(safeStepIndex + 1, snapshots);
      setStepIndex(next);
      replaceStepQuery(next, snapshots);
    }, speedMs);
    return () => clearTimeout(timer);
  }, [isPlaying, safeStepIndex, maxIndex, speedMs, snapshots]);

  const boardView = useMemo(() => {
    if (!current) return null;
    if (showRecap) return projectGodView(current.state);
    if (perspectiveId === "observer") return current.observerView;
    return projectGameStateForViewer(current.state, perspectiveId);
  }, [current, showRecap, perspectiveId]);

  function setReplayStep(index: number): void {
    const next = clampStepIndex(index, snapshots);
    setStepIndex(next);
    replaceStepQuery(next, snapshots);
  }

  if (!fixture || !current || !boardView) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-primary p-6 text-text-primary">
        <section className="max-w-sm rounded-md border border-border-primary bg-surface-secondary p-6 text-center">
          <h1 className="mb-2 font-semibold text-lg">
            리플레이를 찾을 수 없습니다
          </h1>
          <p className="mb-4 text-sm text-text-secondary">{scenario}</p>
          <button
            type="button"
            className="rounded-md bg-accent-blue px-4 py-2 font-semibold text-sm text-white"
            onClick={() => navigateTo("/")}
          >
            닫기
          </button>
        </section>
      </div>
    );
  }

  const state = current.state;
  const outcome = state.gameOutcome;
  const phaseLabel = phaseLabels.get(state.phase) ?? state.phase;
  const phaseChip = PHASE_MODE_CHIPS[state.phase] ?? state.phase;
  const outcomeTeams =
    outcome && outcome.winningTeams.length > 0
      ? outcome.winningTeams
          .map((team) => teamLabels.get(team) ?? team)
          .join(" · ")
      : "없음 (시민편 패배)";

  return (
    <div
      data-testid="one-night-ultimate-werewolf-replay-screen"
      className="min-h-screen px-3 py-4 sm:px-6"
      style={{ background: "var(--cn-onuw-bg)" }}
    >
      <div className="mx-auto flex max-w-xl flex-col gap-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="m-0 font-bold text-lg text-text-primary">
              원나잇 얼티밋 웨어울프 리플레이
            </h1>
            <p className="mt-0.5 text-text-muted text-xs">
              {fixture.players.map((p) => p.name).join(" · ")} · 시뮬레이션
            </p>
          </div>
          <button
            type="button"
            className="rounded-md border border-border-primary bg-surface-secondary px-3 py-1.5 text-text-secondary text-xs"
            onClick={() => navigateTo("/")}
          >
            ‹ 뒤로
          </button>
        </div>

        {outcome ? (
          <div
            data-testid="one-night-ultimate-werewolf-replay-result"
            className="rounded-md border border-accent-blue-light bg-accent-blue-bg px-4 py-3 text-center font-semibold text-accent-blue text-sm"
          >
            🏆 {outcomeReasonLabels.get(outcome.reason) ?? outcome.reason} ·
            승리: {outcomeTeams}
          </div>
        ) : (
          <div className="rounded-md border border-border-subtle bg-surface-secondary px-4 py-2 text-center text-sm text-text-secondary">
            {phaseChip} · 페이즈{" "}
            <span className="font-semibold">{phaseLabel}</span>
          </div>
        )}

        <div className="flex items-center justify-between gap-2 text-text-muted text-xs">
          <label className="flex items-center gap-2">
            시점
            <select
              data-testid="one-night-ultimate-werewolf-replay-viewer-select"
              value={showRecap ? "recap" : perspectiveId}
              disabled={showRecap}
              onChange={(event) => setPerspectiveId(event.target.value)}
              className="rounded-md border border-border-primary bg-surface-primary px-2 py-1 text-text-primary"
            >
              <option value="observer">관전자 (비공개 가림)</option>
              {fixture.players.map((player) => (
                <option key={player.id} value={player.id}>
                  {player.name} 시점
                </option>
              ))}
              {showRecap ? <option value="recap">전체 공개</option> : null}
            </select>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              data-testid="one-night-ultimate-werewolf-replay-recap-toggle"
              data-active={showRecap ? "true" : "false"}
              checked={showRecap}
              onChange={(event) => setShowRecap(event.target.checked)}
            />
            전체 공개 (복기)
          </label>
        </div>

        <OneNightRosterBoard
          view={boardView}
          highlightPlayerId={
            perspectiveId !== "observer" && !showRecap ? perspectiveId : null
          }
        />

        <OneNightEventLog
          entries={cumulativeLogs[safeStepIndex] ?? []}
          expanded={logExpanded}
          onToggle={() => setLogExpanded((value) => !value)}
        />

        <div
          data-testid="one-night-ultimate-werewolf-replay-toolbar"
          className="flex flex-col gap-2 rounded-md border border-border-subtle bg-surface-secondary p-3"
        >
          <p
            data-testid="one-night-ultimate-werewolf-replay-event-summary"
            className="text-center text-sm text-text-primary"
          >
            {eventSummary(current, namesByPlayerId)}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="one-night-ultimate-werewolf-replay-prev"
              disabled={safeStepIndex === 0}
              onClick={() => {
                setIsPlaying(false);
                setReplayStep(safeStepIndex - 1);
              }}
              className="rounded-md border border-border-primary bg-surface-primary px-3 py-2 font-semibold text-sm text-text-primary disabled:opacity-40"
            >
              ◀ 이전
            </button>
            <button
              type="button"
              data-testid="one-night-ultimate-werewolf-replay-play-toggle"
              disabled={safeStepIndex >= maxIndex}
              onClick={() => setIsPlaying((value) => !value)}
              className="rounded-md bg-accent-blue px-3 py-2 font-semibold text-sm text-white disabled:opacity-40"
            >
              {isPlaying ? "⏸ 일시정지" : "▶ 자동재생"}
            </button>
            <button
              type="button"
              data-testid="one-night-ultimate-werewolf-replay-next"
              disabled={safeStepIndex === maxIndex}
              onClick={() => {
                setIsPlaying(false);
                setReplayStep(safeStepIndex + 1);
              }}
              className="rounded-md border border-border-primary bg-surface-primary px-3 py-2 font-semibold text-sm text-text-primary disabled:opacity-40"
            >
              다음 ▶
            </button>
            <span
              data-testid="one-night-ultimate-werewolf-replay-step-count"
              className="ml-auto text-text-secondary text-xs"
            >
              {safeStepIndex + 1}/{snapshots.length}
            </span>
          </div>
          <input
            type="range"
            min={0}
            max={maxIndex}
            value={safeStepIndex}
            data-testid="one-night-ultimate-werewolf-replay-step-slider"
            onChange={(event) => {
              setIsPlaying(false);
              setReplayStep(Number(event.target.value));
            }}
            className="w-full"
          />
          <div className="flex items-center justify-end text-text-muted text-xs">
            <label className="flex items-center gap-2">
              재생 속도
              <select
                data-testid="one-night-ultimate-werewolf-replay-speed"
                value={speedMs}
                onChange={(event) => setSpeedMs(Number(event.target.value))}
                className="rounded-md border border-border-primary bg-surface-primary px-2 py-1 text-text-primary"
              >
                {SPEED_OPTIONS.map((option) => (
                  <option key={option.ms} value={option.ms}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}
